import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken, create } from "axios";

import { MS_IN, MS_IN_SECOND } from "./constants";
import { hasValue } from "./general";

const DEFAULT_TIMEOUT = 5 * MS_IN.MINUTE;

/** Make a POST request */
export async function Post<TOut>(url: string, params?: object | string, data?: unknown, config?: AxiosRequestConfig) {
	return await execute((a) => a.post<TOut>(url, data, { params, ...config }));
}

export async function Put<TOut>(url: string, params?: object | string, data?: unknown, config?: AxiosRequestConfig) {
	return await execute((a) => a.put<TOut>(url, data, { params, ...config }));
}

/** Make a GET request; array params are serialized with indexes (`ids[0]=1&ids[1]=2`) */
export async function Get<TOut>(url: string, params?: object | string, config?: AxiosRequestConfig): Promise<TOut> {
	return await execute((a) => a.get<TOut>(url, { params, paramsSerializer: { indexes: true }, ...config }));
}

/** Make a DELETE request to `url/id`, or `url/` when there is no id */
export async function Delete<TOut>(url: string, id: number, config?: AxiosRequestConfig) {
	if (!url.endsWith("/")) {
		url += "/";
	}
	if (hasValue(id)) url += id;

	return await execute((a) => a.delete<TOut>(url, config));
}

/** Fires a ping (Get) with a 1-second timeout to verify connection */
export async function Ping<TOut>(url: string) {
	return await execute((a) => a.get<TOut>(url), MS_IN_SECOND);
}

async function execute<TOut>(
	fn: (api: AxiosInstance) => Promise<AxiosResponse<TOut>>,
	timeoutMS = DEFAULT_TIMEOUT,
): Promise<TOut> {
	// A cancel token rather than an AbortSignal, so a caller's `signal` in config cannot replace the timeout.
	// oxlint-disable-next-line import/no-named-as-default-member -- axios types the named CancelToken export only as an interface
	const cancelSource = axios.CancelToken.source();
	const api = getApi(cancelSource.token);

	const timeout = setTimeout(() => cancelSource.cancel(), timeoutMS);

	try {
		const response = await fn(api);
		return response?.data;
	} catch (error) {
		throw handleError(error);
	} finally {
		clearTimeout(timeout);
	}
}

function handleError(error: AxiosError | null | undefined): HttpError {
	const response = error?.response;
	return {
		status: response?.status,
		message: getErrorMessage(response?.data, error),
	};
}

function getErrorMessage(data: unknown, error: AxiosError | null | undefined): string {
	if (typeof data === "string") return data;

	const dataMessage = (data as { message?: unknown } | null | undefined)?.message;
	return typeof dataMessage === "string" ? dataMessage : error?.message;
}

function getApi(cancelToken: CancelToken) {
	return create({ headers: { "No-Auth-Redirect": "NoAuthRedirect" }, withCredentials: true, cancelToken });
}

export interface HttpError {
	status: number;
	message: string;
}

export default { Post, Put, Get, Delete, Ping };
